<template>
    <div>
        <slide-up-modal
            v-if="ownerToDelete"
            ref="ownerDeletionModal"
            modal-id="ownerDeletionModal"
            modal-title="Suppression de bénéficiaire"
            @modal-closed="hideDeletionModal"
        >
            <p>
                Êtes-vous certain de vouloir supprimer le bénéficiaire 
                <strong>{{ ownerToDelete.first_names }} {{ ownerToDelete.last_name }}</strong> ? <br>
            </p>

            <div class="actions" style="margin-top: 20px;">
                <button class="btn btn-complete btn-sm" @click="performOwnerDeletion">Valider</button>
                <button class="btn btn-default btn-sm" @click="hideDeletionModal">Annuler</button>
            </div>
        </slide-up-modal>

        <h3>Bénéficiaires effectifs</h3>

        <div v-if="declarationId && !componentIsLoading">
            <div v-if="!editionFormDisplayed">
                <table class="table table-striped" v-if="owners.length > 0">
                    <thead>
                        <tr>
                            <th>Prénom(s)</th>
                            <th>Nom</th>
                            <th>Pièce d'identité en notre possession</th>
                            <th>Date d'expiration pièce d'identité</th>
                            <th></th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="owner in owners" :key="owner.id">
                            <td>
                                {{ owner.first_names }}
                            </td>
                            
                            <td>
                                {{ owner.last_name }}
                            </td>
                            
                            <td>
                                <span :class="{'text-red bold': !owner.identification_in_our_possession}">
                                    {{ owner.identification_in_our_possession ? 'Oui' : 'Non' }}
                                </span>
                            </td>
                            
                            <td>
                                <span :class="{'text-red bold': !owner.identification_expiration_date || identificationHasExpired(owner)}">
                                    {{ owner.identification_expiration_date | moment-date }}
                                </span>
                            </td>

                            <td style="text-align: right;">
                                <button class="btn btn-default btn-sm"
                                    :disabled="anyFormDisplayed"
                                    @click="displayEditionForm(owner)"
                                >
                                    Modifier
                                </button>

                                <button class="btn btn-default btn-sm"
                                    :disabled="anyFormDisplayed"
                                    @click="displayDeletionModal(owner)"
                                >
                                    Supprimer
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div class="alert alert-info" v-else>
                    <i class="fa fa-info-circle"></i> Aucun bénéficiaire n'a été encore été ajouté
                </div>
            </div>

            <div v-if="!creationFormDisplayed && !editionFormDisplayed" style="text-align: center;">
                <button class="btn btn-complete btn-sm"
                    :disabled="anyFormDisplayed"
                    @click="displayCreationForm"
                >
                    Ajouter un bénéficiaire
                </button>
            </div>
            
            <beneficial-owner-creation-form
                v-else-if="creationFormDisplayed"
                :declaration-id="declarationId"
                @form-closed="hideCreationForm"
                @success="onCreationSuccess"
            >
            </beneficial-owner-creation-form>

            <beneficial-owner-edition-form
                v-else-if="editionFormDisplayed"
                :declaration-id="declarationId"
                :owner-id="ownerToUpdate.id"
                @form-closed="hideEditionForm"
                @success="onUpdateSuccess"
            >
            </beneficial-owner-edition-form>
        </div>

        <component-loader v-else></component-loader>
    </div>
</template>

<script type="text/babel">
    import {mapState, mapActions} from "vuex"
    import SlideUpModal from 'components/Modals/SlideUpModal'
    import BeneficialOwnerCreationForm from './BeneficialOwnerCreationForm'
    import BeneficialOwnerEditionForm from './BeneficialOwnerEditionForm'
    import ComponentLoader from 'components/ComponentLoader'
    
    export default {
        name: 'BeneficialOwnersList',

        props: {
            declarationId: {
                type: Number,
                default: null,
            }
        },

        components: {
            SlideUpModal,
            BeneficialOwnerCreationForm,
            BeneficialOwnerEditionForm,
            ComponentLoader
        },

        data() {
            return {
                componentIsLoading: false,
                anyFormDisplayed: false,
                creationFormDisplayed: false,
                ownerToUpdate: null,
                ownerToDelete: null
            }
        },

        created() {
            if (this.declarationId) {
                this.fetchOwners()
            }
        },

        methods: {
            ...mapActions('beneficialOwners', [
                'broadcastFormDisplay',
                'broadcastFormHide',
                'fetchBeneficialOwners', 
                'deleteBeneficialOwner'
            ]),

            fetchOwners() {
                this.componentIsLoading = true

                this.fetchBeneficialOwners(this.declarationId)
                    .then(() => this.componentIsLoading = false)
            },

            identificationHasExpired(owner) {
                const expirationDate = moment(owner.identification_expiration_date)

                return expirationDate.isBefore(moment())
            },

            displayCreationForm() {
                this.creationFormDisplayed = true
                this.broadcastFormDisplay()
            },
            
            hideCreationForm() {
                this.creationFormDisplayed = false
                this.broadcastFormHide()
            },

            onCreationSuccess() {
                this.hideCreationForm()
                this.$emit('beneficial-owner-created')
            },
            
            displayEditionForm(owner) {
                this.ownerToUpdate = owner
                this.broadcastFormDisplay()
            },
            
            hideEditionForm() {
                this.ownerToUpdate = null
                this.broadcastFormHide()
            },

            onUpdateSuccess() {
                this.hideEditionForm()
                this.$emit('beneficial-owner-updated')
            },

            displayDeletionModal(owner) {
                this.ownerToDelete = owner
            },

            hideDeletionModal() {
                this.ownerToDelete = null
            },

            performOwnerDeletion() {
                if (this.ownerToDelete) {
                    this.deletionModal.isLoading = true
    
                    this.deleteBeneficialOwner(this.ownerToDelete)
                        .then(() => {
                            this.ownerToDelete = null
                            
                            this.$emit('beneficial-owner-deleted')
                        })
                        .catch(() => {})
                        .then(() => this.deletionModal.isLoading = false)
                }
            }
        },

        computed: {
            ...mapState('beneficialOwners', {
                'stateFormDisplayed': 'formDisplayed',
                'owners': 'beneficialOwners'
            }),

            editionFormDisplayed() {
                return !! this.ownerToUpdate
            },

            deletionModal() {
                return this.$refs.ownerDeletionModal
            },
        },

        watch: {
            declarationId(newValue, oldValue) {
                if (newValue && newValue != oldValue) {
                    this.fetchOwners()
                }
            },

            stateFormDisplayed(newValue, oldValue) {
                this.anyFormDisplayed = newValue
            }
        }
    }
</script>

<style lang="scss" scoped>
    td, th {
        vertical-align: middle!important;
    }

    .alert-info {
        background-color: #d9edf7;
        border-color: #d9edf7;
        color: #31708f;
    }
</style>



// WEBPACK FOOTER //
// client/components/BeneficialOwners/BeneficialOwnersList.vue